import { z } from "zod/v4";

export const taxSphereSchema = z.enum([
	"ideell",
	"vermoegensverwaltung",
	"zweckbetrieb",
	"wirtschaftlich",
]);

export const accountTypeSchema = z.enum(["income", "expense", "asset"]);

export const bookingTypeSchema = z.enum(["income", "expense"]);

export const referenceTypeSchema = z.enum(["invoice_payment", "expense", "manual"]);

export const accountResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	number: z.string(),
	name: z.string(),
	account_type: accountTypeSchema,
	tax_sphere: taxSphereSchema,
	is_system: z.boolean(),
	is_active: z.boolean(),
	parent_number: z.string().nullable().optional(),
	sort_order: z.number(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const bookingResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	account_id: z.string(),
	booking_type: bookingTypeSchema,
	amount_cents: z.number(),
	currency: z.string(),
	description: z.string(),
	booking_date: z.coerce.date(),
	tax_sphere: taxSphereSchema,
	reference_type: referenceTypeSchema.nullable().optional(),
	reference_id: z.string().nullable().optional(),
	receipt_url: z.string().nullable().optional(),
	created_by: z.string(),
	created_at: z.coerce.date(),
});

export const expenseResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	account_id: z.string(),
	amount_cents: z.number(),
	currency: z.string(),
	description: z.string(),
	vendor: z.string().nullable().optional(),
	expense_date: z.coerce.date(),
	receipt_url: z.string().nullable().optional(),
	created_by: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const fiscalYearResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	year: z.number(),
	start_date: z.coerce.date(),
	end_date: z.coerce.date(),
	is_closed: z.boolean(),
	created_at: z.coerce.date(),
});

export const accountTotalSchema = z.object({
	number: z.string(),
	name: z.string(),
	total_cents: z.number(),
});

export const categoryReportSchema = z.object({
	total_cents: z.number(),
	accounts: z.array(accountTotalSchema),
});

export const sphereReportSchema = z.object({
	sphere: taxSphereSchema,
	label: z.string(),
	income: categoryReportSchema,
	expenses: categoryReportSchema,
	surplus_cents: z.number(),
});

export const euerReportSchema = z.object({
	year: z.number(),
	club_id: z.string(),
	spheres: z.array(sphereReportSchema),
	total_income_cents: z.number(),
	total_expenses_cents: z.number(),
	total_surplus_cents: z.number(),
});

export const cashBookEntrySchema = z.object({
	date: z.coerce.date(),
	description: z.string(),
	account_number: z.string(),
	account_name: z.string(),
	income_cents: z.number(),
	expense_cents: z.number(),
	balance_cents: z.number(),
	tax_sphere: taxSphereSchema,
});

export const sphereSummarySchema = z.object({
	sphere: taxSphereSchema,
	label: z.string(),
	income_cents: z.number(),
	expense_cents: z.number(),
	surplus_cents: z.number(),
});

export const createAccountSchema = z.object({
	number: z.string().min(1, "Account number is required"),
	name: z.string().min(1, "Account name is required"),
	account_type: accountTypeSchema,
	tax_sphere: taxSphereSchema,
	parent_number: z.string().optional(),
	sort_order: z.number().int().optional(),
});

export const updateAccountSchema = z.object({
	name: z.string().min(1).optional(),
	is_active: z.boolean().optional(),
	sort_order: z.number().int().optional(),
});

export const createBookingSchema = z.object({
	account_id: z.string().min(1, "Account is required"),
	booking_type: bookingTypeSchema,
	amount_cents: z.number().int().min(1, "Amount must be positive"),
	currency: z.string().optional(),
	description: z.string().min(1, "Description is required"),
	booking_date: z.string().min(1, "Date is required"),
	receipt_url: z.string().optional(),
});

export const updateBookingSchema = z.object({
	account_id: z.string().optional(),
	amount_cents: z.number().int().min(1).optional(),
	description: z.string().min(1).optional(),
	booking_date: z.string().optional(),
	receipt_url: z.string().optional(),
});

export const createExpenseSchema = z.object({
	account_id: z.string().min(1, "Account is required"),
	amount_cents: z.number().int().min(1, "Amount must be positive"),
	currency: z.string().optional(),
	description: z.string().min(1, "Description is required"),
	vendor: z.string().optional(),
	expense_date: z.string().min(1, "Date is required"),
	receipt_url: z.string().optional(),
});

export const updateExpenseSchema = z.object({
	account_id: z.string().optional(),
	amount_cents: z.number().int().min(1).optional(),
	description: z.string().min(1).optional(),
	vendor: z.string().optional(),
	expense_date: z.string().optional(),
	receipt_url: z.string().optional(),
});

export const createFiscalYearSchema = z.object({
	year: z.number().int().min(2000).max(2100),
	start_date: z.string().min(1, "Start date is required"),
	end_date: z.string().min(1, "End date is required"),
});
